/**
 * Drop-off Engagement Email Templates
 *
 * Pure TypeScript module used by:
 *   1. The dev-only preview at /labs/dropoff-email-preview
 *   2. (Forthcoming) the Supabase scheduled function that scans
 *      labs_user_journey_milestones / labs_user_sessions for users stuck
 *      at a given stage past its delay window, inserts an outreach row,
 *      and hands the email to Resend.
 *
 * SYNC: this module is the source of truth. When the Edge Function is
 * built, a literal copy will live alongside it under
 * supabase/functions/send-dropoff-email/template.ts and the two must be
 * edited together (same pattern as authenticitySurveyEmail.ts).
 *
 * Voice constraints (locked, inherited from Phase 2):
 *   - No em dashes, no en dashes
 *   - No "if / then" constructions
 *   - No contractions
 *   - Team voice, not first-person Max
 *   - Sign-off: "The Stax Team"
 *
 * Visual system: the "Apple-style dark premium" design (April 2026
 * refresh). Page background is near-black #0B0F14; the card sits on top
 * at #0E141C with a hairline #1C2433 border and 12px radius. The hero
 * is a full-bleed dark band with a constellation backdrop. The top row
 * carries the Stax mark and "Stax Labs" wordmark on the left and a
 * soft-blue pill badge on the right with the per-stage "kind" label.
 * Below the row sits a large white Instrument Serif headline that is
 * unique to each stage, rendered over the constellation. Body copy is
 * white #F5F7FA at 17px / line-height 1.65; the greeting line sits a
 * little heavier. The CTA is a single solid blue (#0071E3) pill with
 * white text. Closing paragraphs use the secondary gray #A8B0BD, and
 * the sign-off sits below a faint white-rule hairline. Type is the
 * Apple system stack (SF Pro Display / SF Pro Text, with Inter and
 * Helvetica Neue fallbacks) for everything except the hero headline,
 * which uses Instrument Serif loaded via Google Fonts.
 *
 * The two visual assets (logo + starry hero background) live in a
 * sibling module so this file stays readable. For production the data
 * URIs in that file should be swapped for CDN URLs before send.
 */

import { LOGO_SRC, STARRY_BG } from './dropoffEmailAssets'

export type DropoffStageId =
  | 'approved-no-signup'
  | 'signed-up-no-login'
  | 'builder-no-strategy'
  | 'backtest-no-deploy'
  | 'deploy-no-paid'
  | 'churn-reengage'
  | 'performance-snapshot'

export const DROPOFF_STAGE_IDS: DropoffStageId[] = [
  'approved-no-signup',
  'signed-up-no-login',
  'builder-no-strategy',
  'backtest-no-deploy',
  'deploy-no-paid',
  'churn-reengage',
  'performance-snapshot',
]

export interface RenderedEmail {
  subject: string
  html: string
  text: string
}

export interface BuildParams {
  firstName: string
  recentChanges?: string
  /**
   * Optional URL or data URI for an equity-curve chart, used by the
   * performance-snapshot stage. In dev preview the value is the
   * embedded placeholder; in production the scheduled function will
   * pass a per-recipient chart rendered server-side.
   */
  chartImageSrc?: string
  /**
   * Pre-formatted week-to-date pct shown in the performance card
   * header (e.g. "+6.4%"). Required when chartImageSrc is set;
   * ignored otherwise.
   */
  summaryReturn?: string
  /**
   * Caption next to the value pill. Defaults to "this week" when
   * omitted.
   */
  summaryRange?: string
  /**
   * The recipient's referral code. When supplied (currently only the
   * signed-up-no-login stage uses it), the renderer injects a referral
   * card between the trailing prose and the CTA. The recipient must
   * already have an auth.users + labs_subscriptions row by the time
   * this email fires, since apply_referral() rejects codes without one.
   */
  referralCode?: string
}

export interface DropoffStageDef {
  id: DropoffStageId
  title: string
  triggerSummary: string
  defaultDelay: string
  /** Top-right pill inside the dark hero block. */
  kind: string
  /**
   * Large serif headline rendered beneath the wordmark row inside the
   * hero. One short, declarative sentence per stage, written in the
   * locked Phase 2 voice (no em dashes, no contractions, team voice).
   */
  headline: string
  /**
   * Inbox subject line. Either a static string or a function over
   * BuildParams when the subject needs a per-recipient value (currently
   * the performance-snapshot stage, which interpolates summaryReturn).
   */
  subject: string | ((params: BuildParams) => string)
  /**
   * Hidden preheader text shown by inbox clients next to the subject.
   * Must extend the subject's thought, not repeat it. Voice rules apply.
   */
  preheader: string
  ctaLabel: string
  ctaUrl: string
  buildBody: (params: BuildParams) => { before: string[]; after: string[] }
}

/**
 * Default rotating-changes paragraph for the churn re-engagement email.
 * Edit this constant whenever a meaningful update ships. Same edit-in-code
 * pattern as the Q5 phrase map in authenticitySurveyEmail.ts.
 */
export const RECENT_CHANGES_DEFAULT =
  'The optimizer now searches across multi-factor combinations. The ' +
  'builder ships with community-vetted presets. The backtest window ' +
  'has been extended to cover a longer history.'

export const DROPOFF_STAGES: Record<DropoffStageId, DropoffStageDef> = {
  'approved-no-signup': {
    id: 'approved-no-signup',
    title: 'Approved, no signup',
    triggerSummary:
      'labs_waitlist.status = approved AND no auth.users row for that email',
    defaultDelay: '24 hours after approval',
    kind: 'Approved',
    headline: 'The door is open.',
    subject: 'Approved. The seat is open.',
    preheader: 'One minute of setup. The signup opens with the approval attached.',
    ctaLabel: 'Take your seat',
    ctaUrl: 'https://staxlabs.org/labs/signup',
    buildBody: ({ firstName }) => ({
      before: [
        `Hi ${firstName},`,
        'The application is approved. One click opens the signup with the approval attached.',
        'The first backtest takes about two minutes. The members who go furthest tend to be the ones who set up the same day.',
      ],
      after: [
        'Replies reach a real person.',
      ],
    }),
  },

  'signed-up-no-login': {
    id: 'signed-up-no-login',
    title: 'Signed up, no first login',
    triggerSummary:
      'auth.users created AND labs_user_journey_milestones.first_login_at IS NULL',
    defaultDelay: '48 hours after account creation',
    kind: 'Welcome',
    headline: 'Two minutes to a backtest.',
    subject: 'Two minutes to your first backtest.',
    preheader: 'Pick your filters, choose your timing, hit Backtest. The dashboard shows the equity curve before the coffee gets cold.',
    ctaLabel: 'Open the strategy builder',
    ctaUrl: 'https://staxlabs.org/labs/home?view=strategy-builder',
    buildBody: ({ firstName }) => ({
      before: [
        `Hi ${firstName},`,
        'The account is live. The fastest way to feel the platform is a backtest in the strategy builder.',
        'Pick your filters. Choose your timing. Hit Backtest. The saved strategy lands on the dashboard in under two minutes.',
      ],
      after: [
        'Replies reach a real person.',
      ],
    }),
  },

  'builder-no-strategy': {
    id: 'builder-no-strategy',
    title: 'Builder entry, no strategy saved',
    triggerSummary:
      'builder_direct_entry_at OR tutorial_started_at set AND first_strategy_created_at IS NULL',
    defaultDelay: '24 hours after entering the builder',
    kind: 'Reminder',
    headline: 'The first strategy is the hardest.',
    subject: 'The first strategy is the hardest.',
    preheader: 'Pick your filters, choose your timing, hit Backtest. The dashboard takes it from there.',
    ctaLabel: 'Resume in the builder',
    ctaUrl: 'https://staxlabs.org/labs/home?view=strategy-builder',
    buildBody: ({ firstName }) => ({
      before: [
        `Hi ${firstName},`,
        'The first strategy is the hardest. The next is iteration.',
        'Pick your filters. Choose your timing. Hit Backtest. The saved strategy lives on the dashboard, ready to fork, optimize, or deploy.',
      ],
      after: [
        'Replies reach a real person.',
      ],
    }),
  },

  'backtest-no-deploy': {
    id: 'backtest-no-deploy',
    title: 'Backtest, no deploy',
    triggerSummary:
      'first_backtest_at set AND first_deployment_at IS NULL',
    defaultDelay: '72 hours after first backtest',
    kind: 'Next step',
    headline: 'From history to real trades.',
    subject: 'One click from live trades.',
    preheader: 'Real trades, real money, executed on the rules from the backtest.',
    ctaLabel: 'Deploy the strategy',
    ctaUrl: 'https://staxlabs.org/labs/home',
    buildBody: ({ firstName }) => ({
      before: [
        `Hi ${firstName},`,
        'The first backtest is saved. The next step is the most rewarding one.',
        'Deploying takes the strategy live. Real trades, real money, the rules from the backtest running on the open market.',
        'Most members deploy the same week as the first backtest.',
      ],
      after: [
        'Replies reach a real person.',
      ],
    }),
  },

  'deploy-no-paid': {
    id: 'deploy-no-paid',
    title: 'Deploy, no paid conversion',
    triggerSummary:
      'first_deployment_at set AND first_paid_conversion_at IS NULL',
    // Default 10 days. Revisit between 7 and 14 days based on the
    // engagement analytics surfaced by labs_get_journey_funnel_v2 once a
    // cohort of free deployments has accumulated.
    defaultDelay:
      '10 days after first deployment (default; revisit between 7 and 14 days as analytics accumulate)',
    kind: 'On your tier',
    headline: 'The room behind the door.',
    subject: 'Beyond the first deployment.',
    preheader: 'A portfolio of strategies, the optimizer, longer history. The free deployment keeps running.',
    ctaLabel: 'See the tiers',
    ctaUrl: 'https://staxlabs.org/labs/home?view=pricing',
    buildBody: ({ firstName }) => ({
      before: [
        `Hi ${firstName},`,
        'The first deployment is running. The equity curve is yours to keep watching on the free tier for as long as the strategy lives.',
        'A paid tier opens a portfolio of deployments, a longer backtest window, and the optimizer. The free tier is the entrance. The paid tier is the work surface.',
        // Verify with product before send: the referral-system rewards
        // (FinBucks, deploy slots, optimizer runs) granted via
        // labs_check_and_grant_referral_tier() must survive the free
        // → paid transition for this claim to be accurate.
        'Most members upgrade once a second strategy is on the dashboard. Every referral reward carries into the first paid month.',
      ],
      after: [
        'Questions are welcome. Replies reach a real person.',
      ],
    }),
  },

  'churn-reengage': {
    id: 'churn-reengage',
    title: 'Churn re-engagement',
    triggerSummary:
      '30 days since labs_user_sessions.last_activity_at; current_stage flips to churned',
    defaultDelay: '30 days of session inactivity',
    // Pill text avoids the contraction "What's new" so the dark-hero
    // family stays consistent with the locked voice rules (no contractions).
    kind: 'Latest',
    headline: 'The platform has moved.',
    // The subject names a count ("Three things"). The count must match
    // the number of distinct claims in RECENT_CHANGES_DEFAULT (currently
    // three: optimizer, presets, longer backtest window). When that
    // constant is edited, update the subject in lockstep.
    subject: 'Three things changed since you were last here.',
    preheader: 'The optimizer is faster. The presets are community-vetted. The saved strategies are exactly where they were left.',
    ctaLabel: 'Return to Stax Labs',
    ctaUrl: 'https://staxlabs.org/labs/home',
    buildBody: ({ firstName, recentChanges }) => {
      const changes =
        recentChanges && recentChanges.trim().length > 0
          ? recentChanges.trim()
          : RECENT_CHANGES_DEFAULT
      return {
        before: [
          `Hi ${firstName},`,
          'A few weeks since the last session, and the platform has moved.',
          changes,
          'The account, the saved strategies, and the deployments are exactly where they were left. The return takes one click.',
        ],
        after: [
          'The team would be happy to see you back. Replies reach a real person.',
        ],
      }
    },
  },

  'performance-snapshot': {
    id: 'performance-snapshot',
    title: 'Performance snapshot',
    triggerSummary:
      'pg_cron job send-performance-snapshot fires weekly; recipient set is ' +
      'labs_get_performance_snapshot_recipients() (users with >=1 active ' +
      'deployment)',
    defaultDelay: 'Every Thursday at 21:00 UTC (post US market close)',
    kind: 'Snapshot',
    headline: 'Here is the week.',
    // Spotify-style: lead the inbox with the user's actual return.
    // Renders as e.g. "This week: +6.4%." or "This week: -2.3%." Honest
    // either direction (Strava pattern), no fake celebration.
    subject: ({ summaryReturn }) =>
      `This week: ${summaryReturn && summaryReturn.trim().length > 0 ? summaryReturn.trim() : '+0.0%'}.`,
    preheader: 'The full breakdown, the trades, and the per-strategy result are on the dashboard.',
    ctaLabel: 'Open the full report',
    ctaUrl: 'https://staxlabs.org/labs/home',
    buildBody: ({ firstName }) => ({
      before: [
        `Hi ${firstName},`,
        'Here is how your strategies have moved this week.',
      ],
      after: [
        'The full breakdown is on the dashboard. The next snapshot lands next Thursday.',
      ],
    }),
  },
}

/* ─── Style tokens, Apple-style dark premium system ─────────────── */

// Single-quote the family names so this string can be interpolated into
// inline style="..." attributes without prematurely closing the attribute.
//
// Exported so siblings in the transactional family (waitlist welcome, etc.)
// share one token set rather than duplicating it.
export const SANS =
  "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', " +
  "'Inter', 'Helvetica Neue', Arial, sans-serif"

// Used only for the hero headline. Falls back to local high-contrast
// serifs when the Google Fonts link is stripped by the email client.
export const SERIF_DISPLAY =
  "'Instrument Serif', 'Iowan Old Style', Georgia, 'Times New Roman', serif"

export const FONTS_LINK =
  'https://fonts.googleapis.com/css2?family=Instrument+Serif&display=swap'

export const COLOR = {
  page:              '#0B0F14',
  card:              '#0E141C',
  border:            '#1C2433',
  borderSubtle:      'rgba(255, 255, 255, 0.08)',
  textPrimary:       '#F5F7FA',
  textSecondary:     '#A8B0BD',
  textMuted:         '#7B8494',
  wordmark:          '#F0F6FC',
  accentSoft:        '#7DBBFF',
  accentBgFaint:     'rgba(41, 151, 255, 0.08)',
  accentBorderFaint: 'rgba(41, 151, 255, 0.32)',
  ctaBg:             '#0071E3',
  ctaText:           '#FFFFFF',
} as const

/**
 * Produces the HTML and plaintext bodies for a given drop-off stage and
 * recipient.
 */
export function renderDropoffEmail(
  stageId: DropoffStageId,
  params: BuildParams,
): RenderedEmail {
  const stage = DROPOFF_STAGES[stageId]
  const name =
    params.firstName && params.firstName.trim().length > 0
      ? params.firstName.trim()
      : 'there'

  const resolvedParams = { ...params, firstName: name }
  const { before, after } = stage.buildBody(resolvedParams)

  const subject =
    typeof stage.subject === 'function'
      ? stage.subject(resolvedParams)
      : stage.subject

  const text = [
    ...before,
    `${stage.ctaLabel}: ${stage.ctaUrl}`,
    ...after,
    '',
    'The Stax Team',
    '',
    'Reply STOP to opt out of Stax product emails.',
  ].join('\n\n')

  const html = renderHtml(
    before,
    after,
    stage.ctaLabel,
    stage.ctaUrl,
    stage.headline,
    stage.preheader,
    params.chartImageSrc,
    params.summaryReturn,
    params.summaryRange,
    params.referralCode,
  )

  return { subject, html, text }
}

function renderHtml(
  before: string[],
  after: string[],
  ctaLabel: string,
  ctaUrl: string,
  headline: string,
  preheader: string,
  chartImageSrc?: string,
  summaryReturn?: string,
  summaryRange?: string,
  referralCode?: string,
): string {
  // The first "before" paragraph is the greeting line (e.g. "Maximus,").
  // It carries a slightly heavier weight so the email opens with a clear
  // addressee, then settles into regular body weight for the prose.
  const [greetingLine, ...prose] = before

  const greetingParagraph = greetingLine
    ? `<p style="font-family:${SANS}; font-size:17px; font-weight:600; ` +
      `line-height:1.4; letter-spacing:-0.015em; color:${COLOR.textPrimary}; ` +
      `margin:0 0 18px 0;">${escapeHtml(greetingLine)}</p>`
    : ''

  const bodyParagraph = (p: string) =>
    `<p style="font-family:${SANS}; font-size:17px; font-weight:400; ` +
    `line-height:1.65; letter-spacing:-0.01em; color:${COLOR.textPrimary}; ` +
    `margin:0 0 18px 0;">${escapeHtml(p)}</p>`

  const closingParagraph = (p: string) =>
    `<p style="font-family:${SANS}; font-size:14px; font-weight:400; ` +
    `line-height:1.65; letter-spacing:-0.005em; color:${COLOR.textSecondary}; ` +
    `margin:0 0 14px 0;">${escapeHtml(p)}</p>`

  const proseHtml  = prose.map(bodyParagraph).join('\n              ')
  const afterHtml  = after.map(closingParagraph).join('\n              ')

  // Optional performance card. Only rendered when the caller supplies a
  // chart image (currently the performance-snapshot stage). Carries the
  // "Strategy return" value pill, four range tabs (1W active; 1M / 3M /
  // ALL link out to the dashboard), and the equity chart beneath. JS is
  // stripped by every email client, so the tabs are anchor links — the
  // dashboard handles the actual range switch when the recipient lands.
  const chartHtml = chartImageSrc
    ? renderPerfCard(chartImageSrc, summaryReturn || '+0.0%', summaryRange || 'this week')
    : ''

  // Optional referral card. Currently the signed-up-no-login stage is
  // the only one that supplies a code, since by that point the recipient
  // has the auth.users + labs_subscriptions row that apply_referral()
  // requires before it can credit the referrer.
  const referralHtml = referralCode
    ? renderReferralCard(referralCode)
    : ''

  // Centered solid-blue pill button. Unicode arrow → at the end. Inline-
  // block with explicit padding so the shape is preserved across clients.
  const ctaHtml =
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" ` +
    `style="margin:32px auto 28px auto;"><tr><td align="center">` +
    `<a href="${escapeAttr(ctaUrl)}" style="display:inline-block; ` +
    `background:${COLOR.ctaBg}; color:${COLOR.ctaText}; ` +
    `font-family:${SANS}; font-size:15px; font-weight:700; ` +
    `letter-spacing:-0.005em; text-decoration:none; padding:14px 32px; ` +
    `border-radius:10px;">${escapeHtml(ctaLabel)} &#8594;</a>` +
    `</td></tr></table>`

  // Hero block: full-bleed within the card, page-dark fill with the
  // constellation image laid over the top. The top row carries the
  // Stax mark and wordmark on the left and nothing on the right.
  // Beneath the row sits a large white Instrument Serif headline,
  // unique per stage, rendered over the constellation. The image is
  // decorative; the dark fill is the real signal and shows even when
  // the image is stripped by the client.
  const heroHtml =
    `        <tr>` +
    `          <td class="stax-hero-cell" bgcolor="${COLOR.page}" ` +
    `style="background-color:${COLOR.page}; ` +
    `background-image:url('${escapeAttr(STARRY_BG)}'); ` +
    `background-position:center center; background-size:cover; background-repeat:no-repeat; ` +
    `padding:28px 32px 40px 32px; border-bottom:1px solid ${COLOR.borderSubtle};">` +
    `            <div style="line-height:0;">` +
    `              <img src="${escapeAttr(LOGO_SRC)}" width="32" height="32" alt="Stax" ` +
    `style="display:inline-block; vertical-align:middle; border:0; outline:none; text-decoration:none;">` +
    `              <span style="font-family:${SANS}; font-size:15px; font-weight:700; ` +
    `color:${COLOR.wordmark}; letter-spacing:0.3px; vertical-align:middle; padding-left:12px; line-height:1;">Stax Labs</span>` +
    `            </div>` +
    `            <h1 class="stax-hero-headline" style="font-family:${SERIF_DISPLAY}; font-size:48px; font-weight:400; ` +
    `line-height:1.04; letter-spacing:-0.02em; color:${COLOR.textPrimary}; ` +
    `margin:36px 0 0 0;">${escapeHtml(headline)}</h1>` +
    `          </td>` +
    `        </tr>`

  return [
    '<!DOCTYPE html>',
    '<html lang="en"><head>',
    '<meta charset="UTF-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    '<meta name="color-scheme" content="dark">',
    '<meta name="supported-color-schemes" content="dark">',
    `<link href="${FONTS_LINK}" rel="stylesheet">`,
    // Mobile typography. Email clients that honor <style> blocks (Apple Mail,
    // Gmail Android, Outlook 365 mobile) will shrink the hero headline so it
    // doesn't break across 3-4 lines on a phone, and wrap the perf-card range
    // tabs so they don't push off the right edge.
    '<style>',
    '@media only screen and (max-width: 480px) {',
    '  .stax-hero-headline { font-size: 32px !important; line-height: 1.08 !important; margin-top: 24px !important; }',
    '  .stax-hero-cell { padding: 22px 22px 28px 22px !important; }',
    '  .stax-body-cell { padding: 28px 22px 28px 22px !important; }',
    '  .stax-perf-tabs a { padding: 5px 10px !important; font-size: 11px !important; }',
    '}',
    '</style>',
    '<title>Stax Labs</title>',
    '</head>',
    `<body style="margin:0; padding:0; background:${COLOR.page}; ` +
      `-webkit-font-smoothing:antialiased; -moz-osx-font-smoothing:grayscale;">`,
    // Hidden preheader. Inbox clients show this next to the subject in
    // the inbox preview row. The trailing zero-width-non-joiner padding
    // pushes Gmail's auto-extracted preview past any visible body line.
    `  <div style="display:none; font-size:1px; line-height:1px; ` +
      `max-height:0; max-width:0; opacity:0; overflow:hidden; ` +
      `mso-hide:all; color:${COLOR.page};">${escapeHtml(preheader)}` +
      '&nbsp;&zwnj;'.repeat(80) +
      '</div>',
    `  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ` +
      `style="background:${COLOR.page};">`,
    '    <tr>',
    '      <td align="center" style="padding:32px 16px 64px;">',
    `        <table role="presentation" width="560" cellpadding="0" cellspacing="0" border="0" ` +
      `style="max-width:560px; width:100%; background:${COLOR.card}; ` +
      `border:1px solid ${COLOR.border}; border-radius:12px; overflow:hidden;">`,
    heroHtml,
    '          <tr>',
    '            <td class="stax-body-cell" style="padding:36px 32px 36px 32px;">',
    '',
    `              ${greetingParagraph}`,
    `              ${proseHtml}`,
    `              ${chartHtml}`,
    `              ${referralHtml}`,
    `              ${ctaHtml}`,
    `              ${afterHtml}`,
    '',
    `              <div style="height:1px; background:${COLOR.borderSubtle}; margin:18px 0 18px 0;"></div>`,
    `              <p style="font-family:${SANS}; font-style:italic; ` +
      `font-size:14px; font-weight:400; line-height:1.5; letter-spacing:0.01em; ` +
      `color:${COLOR.textSecondary}; margin:0;">The Stax Team</p>`,
    `              <p style="font-family:${SANS}; font-size:12px; ` +
      `font-weight:400; line-height:1.5; letter-spacing:0.01em; ` +
      `color:${COLOR.textSecondary}; margin:20px 0 0 0; opacity:0.65;">` +
      `Reply <strong>STOP</strong> to opt out of Stax product emails.</p>`,
    '            </td>',
    '          </tr>',
    '        </table>',
    '      </td>',
    '    </tr>',
    '  </table>',
    '</body></html>',
  ].join('\n')
}

/* ─── Performance-card helpers ─────────────────────────────────────── */

const PERF_INNER_CARD_BG     = '#0B0D12'
const PERF_INNER_CARD_BORDER = 'rgba(255, 255, 255, 0.06)'
const PERF_LABEL_COLOR       = 'rgba(255, 255, 255, 0.40)'
const PERF_CAPTION_COLOR     = 'rgba(255, 255, 255, 0.30)'
// Cyan for a positive week, red for a negative one. A losing week must read as
// a losing week, in the number and in the chart, never a fixed cyan up-trend.
const PERF_POSITIVE_COLOR    = '#00D1FF'
const PERF_NEGATIVE_COLOR    = '#FF453A'
const PERF_TAB_ACTIVE_BG     = 'rgba(255, 255, 255, 0.10)'
const PERF_TAB_ACTIVE_TEXT   = '#FFFFFF'
const PERF_TAB_INACTIVE_BG   = 'rgba(255, 255, 255, 0.04)'
const PERF_TAB_INACTIVE_TEXT = 'rgba(255, 255, 255, 0.25)'

const PERF_RANGE_LINKS: Record<'1W' | '1M' | '3M' | 'ALL', string> = {
  '1W':  'https://staxlabs.org/labs/home?range=1w',
  '1M':  'https://staxlabs.org/labs/home?range=1m',
  '3M':  'https://staxlabs.org/labs/home?range=3m',
  'ALL': 'https://staxlabs.org/labs/home?range=all',
}

// Negative weeks lead with a minus sign; everything else reads as a gain.
function perfReturnColor(summaryReturn: string): string {
  return summaryReturn.trim().startsWith('-') ? PERF_NEGATIVE_COLOR : PERF_POSITIVE_COLOR
}

function renderPerfCard(
  chartImageSrc: string,
  summaryReturn: string,
  summaryRange: string,
): string {
  const valueColor = perfReturnColor(summaryReturn)
  const tab = (label: '1W' | '1M' | '3M' | 'ALL', active: boolean) =>
    `<a class="stax-perf-tab" href="${escapeAttr(PERF_RANGE_LINKS[label])}" style="display:inline-block; ` +
    `font-family:${SANS}; font-size:11px; font-weight:600; ` +
    `letter-spacing:0.04em; text-decoration:none; ` +
    `padding:4px 10px; border-radius:6px; ` +
    `color:${active ? PERF_TAB_ACTIVE_TEXT : PERF_TAB_INACTIVE_TEXT}; ` +
    `background:${active ? PERF_TAB_ACTIVE_BG : PERF_TAB_INACTIVE_BG};">` +
    `${escapeHtml(label)}</a>`

  const headerRow =
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ` +
    `style="margin-bottom:20px;">` +
    `<tr>` +
    `  <td align="left" valign="bottom" style="vertical-align:bottom;">` +
    `    <div style="font-family:${SANS}; font-size:13px; ` +
    `color:${PERF_LABEL_COLOR}; margin-bottom:4px;">Strategy return</div>` +
    `    <span style="font-family:${SANS}; font-size:24px; font-weight:500; ` +
    `letter-spacing:-0.3px; color:${valueColor};">` +
    `${escapeHtml(summaryReturn)}</span>` +
    `    <span style="font-family:${SANS}; font-size:12px; ` +
    `color:${PERF_CAPTION_COLOR}; margin-left:8px;">${escapeHtml(summaryRange)}</span>` +
    `  </td>` +
    `  <td class="stax-perf-tabs" align="right" valign="middle" style="vertical-align:middle; white-space:nowrap;">` +
    `    ${tab('1W', true)} ${tab('1M', false)} ${tab('3M', false)} ${tab('ALL', false)}` +
    `  </td>` +
    `</tr>` +
    `</table>`

  const chartImg =
    `<img src="${escapeAttr(chartImageSrc)}" ` +
    `alt="Equity curve, week to date" ` +
    `style="display:block; width:100%; height:auto; border:0; outline:none;">`

  return (
    `<div style="margin:24px 0 8px 0; background:${PERF_INNER_CARD_BG}; ` +
    `border:1px solid ${PERF_INNER_CARD_BORDER}; border-radius:10px; ` +
    `padding:24px 20px 20px 20px;">` +
    headerRow +
    chartImg +
    `</div>`
  )
}

/**
 * Build a QuickChart.io PNG URL for the recipient's week-to-date equity curve.
 *
 * `labels` are short day labels (e.g. "06-02") and `series` is the normalized
 * percent-from-week-start for each point, so the first value is always 0 and the
 * last value equals the week return shown in the header. The line and fill are
 * colored by the final value's sign: a losing week renders as a red curve that
 * trends down, never the static up-and-to-the-right placeholder. Both the chart
 * and the header number are derived from this same series upstream, so they can
 * never disagree.
 *
 * Sized at 2x the ~536px display box and backed with the inner-card fill so the
 * PNG blends into the card seamlessly. Mirrors buildRecapChartUrl in
 * weeklyRecapEmail.ts (the one other QuickChart consumer in this package).
 */
export function buildPerfSnapshotChartUrl(labels: string[], series: number[]): string {
  const last = series.length > 0 ? series[series.length - 1]! : 0
  const up = last >= 0
  const line = up ? PERF_POSITIVE_COLOR : PERF_NEGATIVE_COLOR
  const fill = up ? 'rgba(0,209,255,0.12)' : 'rgba(255,69,58,0.12)'
  const config = {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          data: series,
          borderColor: line,
          backgroundColor: fill,
          fill: true,
          borderWidth: 3,
          pointRadius: 0,
          tension: 0.3,
        },
      ],
    },
    options: {
      responsive: false,
      layout: { padding: { top: 8, right: 12, bottom: 4, left: 4 } },
      plugins: { legend: { display: false } },
      scales: {
        x: {
          grid: { color: 'rgba(255,255,255,0.06)' },
          ticks: { color: 'rgba(255,255,255,0.35)', font: { size: 16 }, maxTicksLimit: 7 },
        },
        y: {
          grid: { color: 'rgba(255,255,255,0.06)' },
          ticks: { color: 'rgba(255,255,255,0.35)', font: { size: 16 }, callback: "function(v){return v+'%'}" },
        },
      },
    },
  }
  const json = encodeURIComponent(JSON.stringify(config))
  return `https://quickchart.io/chart?c=${json}&w=1072&h=420&bkg=${encodeURIComponent(PERF_INNER_CARD_BG)}&f=png&version=4`
}

/* ─── Referral code inline card ────────────────────────────────────── */

const REFERRAL_INNER_BG     = '#0B0D12'
const REFERRAL_INNER_BORDER = 'rgba(255, 255, 255, 0.06)'
const REFERRAL_LABEL_COLOR  = 'rgba(255, 255, 255, 0.40)'
const REFERRAL_CODE_COLOR   = '#F5F7FA'

function renderReferralCard(referralCode: string): string {
  return (
    `<div style="margin:24px 0 8px 0; background:${REFERRAL_INNER_BG}; ` +
    `border:1px solid ${REFERRAL_INNER_BORDER}; border-radius:10px; ` +
    `padding:24px 28px; text-align:center;">` +
    `<div style="font-family:${SANS}; font-size:11px; font-weight:600; ` +
    `letter-spacing:0.14em; text-transform:uppercase; color:${REFERRAL_LABEL_COLOR}; ` +
    `margin-bottom:10px;">Your referral code</div>` +
    `<div style="font-family:'SF Mono', 'JetBrains Mono', Menlo, monospace; ` +
    `font-size:22px; font-weight:600; letter-spacing:1.5px; ` +
    `color:${REFERRAL_CODE_COLOR}; line-height:1.2;">` +
    `${escapeHtml(referralCode)}</div>` +
    `</div>`
  )
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}
